import { useState, useEffect } from 'react';

const BrowserCheck = ({ children }) => {
  const [isSupported, setIsSupported] = useState(true);
  const [showWarning, setShowWarning] = useState(false);

  useEffect(() => {
    checkBrowserSupport();
  }, []);

  const checkBrowserSupport = () => {
    let supported = true;
    let issues = [];

    // 1. 检测 ES6+ 基本支持
    try {
      // 检测箭头函数（不用 eval）
      const arrowFunc = () => {};
      if (typeof arrowFunc !== 'function') {
        supported = false;
        issues.push('不支持箭头函数');
      }
      
      // 检测 let/const（不用 eval）
      let testLet = 1;
      const testConst = 2;
      if (testLet !== 1 || testConst !== 2) {
        supported = false;
        issues.push('不支持 let/const');
      }
      
      // 检测 Promise
      if (typeof Promise === 'undefined') {
        supported = false;
        issues.push('不支持 Promise');
      }
      
      // 检测 fetch API
      if (typeof fetch === 'undefined') {
        supported = false;
        issues.push('不支持 fetch API');
      }
      
      // 检测 Array.from
      if (typeof Array.from === 'undefined') {
        supported = false;
        issues.push('不支持 Array.from');
      }
      
      // 检测 Object.assign
      if (typeof Object.assign === 'undefined') {
        supported = false;
        issues.push('不支持 Object.assign');
      }
    } catch (e) {
      supported = false;
      issues.push('不支持 ES6+ 语法');
    }

    // 2. 检测 CSS Grid/Flexbox 支持
    const style = document.createElement('div').style;
    if (!('grid' in style) && !('msGrid' in style)) {
      issues.push('CSS Grid 支持有限');
    }
    if (!('flex' in style) && !('webkitFlex' in style)) {
      supported = false;
      issues.push('不支持 Flexbox');
    }

    // 3. 检测 ES Modules 支持
    const supportsModules = 'noModule' in document.createElement('script');
    if (!supportsModules) {
      issues.push('不支持 ES Modules');
    }

    // 4. 检测 Canvas 支持
    const canvas = document.createElement('canvas');
    if (!canvas.getContext || !canvas.getContext('2d')) {
      supported = false;
      issues.push('不支持 Canvas');
    }

    // 5. 检测浏览器版本
    const ua = navigator.userAgent;
    
    // 检测旧版 IE
    if (ua.indexOf('MSIE') !== -1 || ua.indexOf('Trident') !== -1) {
      supported = false;
      issues.push('IE 浏览器不受支持');
    }

    // 检测旧版 Safari (版本 < 12)
    const safariMatch = ua.match(/Version\/(\d+)/);
    if (safariMatch && parseInt(safariMatch[1]) < 12) {
      issues.push('Safari 版本过旧');
    }

    // 检测旧版 Chrome (版本 < 60)
    const chromeMatch = ua.match(/Chrome\/(\d+)/);
    if (chromeMatch && parseInt(chromeMatch[1]) < 60) {
      issues.push('Chrome 版本过旧');
    }

    // 检测旧版 Firefox (版本 < 55)
    const firefoxMatch = ua.match(/Firefox\/(\d+)/);
    if (firefoxMatch && parseInt(firefoxMatch[1]) < 55) {
      issues.push('Firefox 版本过旧');
    }

    if (!supported || issues.length > 0) {
      setIsSupported(supported);
      setShowWarning(true);
      console.log('浏览器兼容性问题:', issues);
    }
  };

  if (!showWarning) {
    return <>{children}</>;
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {/* 警告提示 */}
      <div className={`p-4 ${isSupported ? 'bg-yellow-50 border-b border-yellow-200' : 'bg-red-50 border-b border-red-200'}`}>
        <div className="max-w-4xl mx-auto">
          <div className="flex items-start gap-3">
            <div className={`text-2xl ${isSupported ? 'text-yellow-500' : 'text-red-500'}`}>
              {isSupported ? '⚠️' : '❌'}
            </div>
            <div className="flex-1">
              <h3 className={`font-bold ${isSupported ? 'text-yellow-800' : 'text-red-800'}`}>
                {isSupported ? '浏览器兼容性警告' : '浏览器不受支持'}
              </h3>
              <p className={`mt-1 text-sm ${isSupported ? 'text-yellow-700' : 'text-red-700'}`}>
                {isSupported 
                  ? '您的浏览器可能较旧，部分功能可能无法正常显示。建议升级浏览器以获得最佳体验。'
                  : '您的浏览器过旧，无法正常使用此网站。请升级到现代浏览器。'
                }
              </p>
            </div>
            <button
              onClick={() => setShowWarning(false)}
              className={`px-3 py-1 rounded text-sm ${isSupported ? 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200' : 'bg-red-100 text-red-800 hover:bg-red-200'}`}
            >
              {isSupported ? '仍要继续' : '关闭'}
            </button>
          </div>
        </div>
      </div>

      {/* 浏览器升级建议 */}
      {!isSupported && (
        <div className="p-6">
          <div className="max-w-4xl mx-auto bg-white rounded-lg shadow-lg p-6">
            <h3 className="text-lg font-bold text-gray-800 mb-4">推荐的现代浏览器：</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <a href="https://www.google.com/chrome/" target="_blank" rel="noopener noreferrer" 
                 className="p-4 border rounded-lg hover:bg-gray-50 text-center">
                <div className="text-3xl mb-2">🌐</div>
                <div className="font-medium">Chrome</div>
              </a>
              <a href="https://www.mozilla.org/firefox/" target="_blank" rel="noopener noreferrer"
                 className="p-4 border rounded-lg hover:bg-gray-50 text-center">
                <div className="text-3xl mb-2">🦊</div>
                <div className="font-medium">Firefox</div>
              </a>
              <a href="https://www.microsoft.com/edge" target="_blank" rel="noopener noreferrer"
                 className="p-4 border rounded-lg hover:bg-gray-50 text-center">
                <div className="text-3xl mb-2">💚</div>
                <div className="font-medium">Edge</div>
              </a>
              <a href="https://www.apple.com/safari/" target="_blank" rel="noopener noreferrer"
                 className="p-4 border rounded-lg hover:bg-gray-50 text-center">
                <div className="text-3xl mb-2">🍎</div>
                <div className="font-medium">Safari</div>
              </a>
            </div>
          </div>
        </div>
      )}

      {/* 如果是警告而非完全不支持，仍显示内容 */}
      {isSupported && <>{children}</>}
    </div>
  );
};

export default BrowserCheck;
